import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  INewSubscribeOptions,
  IRequestSubscribeNewResponse,
  requestSubscribeNew,
  TSubscriptionSource,
} from '../../../api/subscribe';
import { IAppState, SubscriptionState, TActions, TSubscriptionState, Dispatch } from '../../../common/state';
import { ISubscriptionsStates } from '../../../common/state/app_state';

import { listTracking, nothingFoundTracking } from '../../../common/components/nothing_found/subscribe/tracking';
import { trackSuccessfulSubscribe } from './tracking';

export type TSubscriptionType = 1 | 2;
export const SubscriptionType = {
  NothingFound: 1 as 2,
  Bottom: 2 as 2,
};
export const SubscriptionSource = {
  Listing: 'listing' as TSubscriptionSource,
  Map: 'map' as TSubscriptionSource,
};

export interface ISubscribeSucceededAction {
  type: 'filters/subscribe/SUBSCRIBE_SUCCEEDED';
  subscriptionType: TSubscriptionType;
}

export interface ISubscribeFailedAction {
  type: 'filters/subscribe/SUBSCRIBE_FAILED';
  subscriptionType: TSubscriptionType;
}

export interface ISubscribeRequestedAction {
  type: 'filters/subscribe/SUBSCRIBE_REQUESTED';
  subscriptionType: TSubscriptionType;
  email: string;
  subscribeNews: boolean;
  source?: TSubscriptionSource;
}

export const subscribe = (
  email: string,
  subscriptionType: TSubscriptionType,
  subscribeNews: boolean,
  source: string,
) => (dispatch: Dispatch) =>
  dispatch({
    type: 'filters/subscribe/SUBSCRIBE_REQUESTED',
    subscriptionType,
    email,
    source,
    subscribeNews,
  });

export function* subscriber(action: ISubscribeRequestedAction) {
  const { subscriptionType } = action;
  const state: IAppState = yield select();

  const {
    filters: { jsonQuery },
    results,
    config,
    httpApi,
  } = state;

  const requestSubscribeOptions = {
    jsonQuery,
    title: results.seo.h1,
    email: action.email,
    source: action.source,
    subscribeNews: action.subscribeNews,
  } as INewSubscribeOptions;

  const offersPerPage = Number(config.get('serp.offersPerPage'));

  try {
    const subscribeResponse: IRequestSubscribeNewResponse = yield call(
      requestSubscribeNew,
      httpApi,
      requestSubscribeOptions,
    );

    if (subscribeResponse.subscribe || subscribeResponse) {
      trackSuccessfulSubscribe(action.email, jsonQuery, subscriptionType, results.aggregatedOffers, offersPerPage);

      if (subscribeResponse && subscribeResponse.auth) {
        if (subscriptionType === SubscriptionType.NothingFound) {
          nothingFoundTracking.trackSubscribeWithRegistrate();
        } else if (subscriptionType === (SubscriptionType.Bottom as TSubscriptionType)) {
          listTracking.trackSubscribeWithRegistrate();
        }
      }

      yield put({
        type: 'filters/subscribe/SUBSCRIBE_SUCCEEDED',
        subscriptionType,
      });
    } else {
      yield put({
        type: 'filters/subscribe/SUBSCRIBE_FAILED',
        subscriptionType,
      });
    }
  } catch (e) {
    yield put({
      type: 'filters/subscribe/SUBSCRIBE_FAILED',
      subscriptionType,
    });
  }
}

export function* subscribeSaga() {
  yield takeLatest('filters/subscribe/SUBSCRIBE_REQUESTED', subscriber);
}

const setSubscriptionState = (
  subscriptionStates: ISubscriptionsStates,
  subscriptionType: TSubscriptionType,
  subscriptionState: TSubscriptionState,
): ISubscriptionsStates => {
  if (subscriptionType === SubscriptionType.Bottom) {
    return {
      ...subscriptionStates,
      bottom: subscriptionState,
    };
  } else {
    return {
      ...subscriptionStates,
      nothingFound: subscriptionState,
    };
  }
};

export function subscribeReducer(state: IAppState, action: TActions): IAppState {
  switch (action.type) {
    case 'filters/subscribe/SUBSCRIBE_REQUESTED':
      return {
        ...state,
        results: {
          ...state.results,
          subscriptionsStates: setSubscriptionState(
            state.results.subscriptionsStates,
            action.subscriptionType,
            SubscriptionState.Fetching,
          ),
        },
      };
    case 'filters/subscribe/SUBSCRIBE_SUCCEEDED':
      return {
        ...state,
        results: {
          ...state.results,
          subscriptionsStates: setSubscriptionState(
            state.results.subscriptionsStates,
            action.subscriptionType,
            SubscriptionState.Succeeded,
          ),
        },
      };
    case 'filters/subscribe/SUBSCRIBE_FAILED':
      return {
        ...state,
        results: {
          ...state.results,
          subscriptionsStates: setSubscriptionState(
            state.results.subscriptionsStates,
            action.subscriptionType,
            SubscriptionState.Failed,
          ),
        },
      };
    default:
      return state;
  }
}
